import { supabase } from "./supabase";

/* The coach's public page — read, edit, publish — on the web.
 *
 * 🔴 THIS IS THE SECOND HALF OF WEB SIGN-UP, not a feature beside it. A parity walk on 2026-09-13
 * found `coach_profiles` referenced NOWHERE under `web/src`, hours after web sign-up shipped — so a
 * coach who created an account in a browser could roster clients and build workouts and **never
 * become visible or able to post**, which is the whole point of having a coaching page. The account
 * was a dead end and nothing said so.
 *
 * ⚠️ **THE PUBLISH RULE IS PORTED, NOT REINVENTED.** `CoachProfileDraft.publishFault` is the
 * authority on the phone; the copy below answers in the same order with the same limits and the
 * same sentences, and `coachProfile.test.ts` executes it. Two rules that merely agree today are
 * this repo's defining bug class — so the ORDER of the checks is part of the port, because it
 * decides which single message a coach is shown when two things are wrong.
 *
 * ⚠️ **NO POLICY CHANGE.** `coach_profiles` already admits the owner's select and upsert — that is
 * what the phone's editor uses. This module adds a reader and a writer, nothing else, which is why
 * it is first in the web port: it is the largest gap with the smallest surface.
 */

export const NAME_LIMIT = 80;
export const HEADLINE_LIMIT = 120;
export const BIO_LIMIT = 2000;
export const CITY_LIMIT = 80;
export const MAX_YEARS = 80;
export const MAX_ACHIEVEMENTS = 12;

export type CoachProfile = {
  displayName: string;
  headline: string;
  bio: string;
  city: string;
  yearsCoaching: number;
  achievements: string[];
  website: string;
  instagram: string;
  avatarUrl: string | null;
  isPublished: boolean;
};

export const EMPTY_PROFILE: CoachProfile = {
  displayName: "", headline: "", bio: "", city: "",
  yearsCoaching: 0, achievements: [], website: "", instagram: "",
  avatarUrl: null, isPublished: false,
};

/* Blank rows are dropped rather than stored — an achievements array with three empty strings
 * renders as three empty rows on a client's screen, and the database CHECK counts them. */
export function keptAchievements(raw: string[]): string[] {
  return raw.map((a) => a.trim()).filter((a) => a !== "");
}

/* An empty text field means "not stated", and the column is nullable for exactly that. Storing ""
 * would make "no website" and "a website I cleared" different values that render identically. */
export function optional(raw: string): string | null {
  const t = raw.trim();
  return t === "" ? null : t;
}

/* `null` means it may be published — the same contract as the phone's `publishFault`.
 *
 * ⚠️ The portrait is OPTIONAL (his 2026-09-12 word, after the upload turned out not to work), so
 * its absence is NOT a fault here. That was a deliberate reversal and copying an older rule would
 * silently reinstate a requirement he removed. */
export function publishFault(p: CoachProfile): string | null {
  const name = p.displayName.trim();
  if (name === "") return "Add the name clients will see.";
  if (name.length > NAME_LIMIT) return tooLong("Name", name.length - NAME_LIMIT);

  const line = p.headline.trim();
  if (line === "") {
    return "Add the one line under your name — it is the only thing the list shows about what you coach.";
  }
  if (line.length > HEADLINE_LIMIT) return tooLong("Headline", line.length - HEADLINE_LIMIT);
  if (p.bio.length > BIO_LIMIT) return tooLong("Bio", p.bio.length - BIO_LIMIT);
  if (p.city.length > CITY_LIMIT) return tooLong("City", p.city.length - CITY_LIMIT);
  if (p.yearsCoaching < 0 || p.yearsCoaching > MAX_YEARS) {
    return `Years coaching has to be between 0 and ${MAX_YEARS}.`;
  }
  const kept = keptAchievements(p.achievements);
  if (kept.length > MAX_ACHIEVEMENTS) {
    const by = kept.length - MAX_ACHIEVEMENTS;
    return `${by} achievement${by === 1 ? "" : "s"} too many — keep the ${MAX_ACHIEVEMENTS} that matter.`;
  }
  return null;
}

function tooLong(field: string, by: number): string {
  return `${field} is ${by} character${by === 1 ? "" : "s"} too long.`;
}

/* ⚠️ **IT REPORTS WHETHER A ROW EXISTS, SEPARATELY FROM WHAT IS IN IT (2026-09-22).** It used to
 * return `EMPTY_PROFILE` when there was no row at all, which is byte-identical to a row somebody
 * saved empty — one value standing for two different answers. Nothing noticed while the only
 * caller was the editor, which treats both the same and correctly so. `CoachSetupPrompt` is the
 * caller that CANNOT: "you have not started" and "you started and did not publish" send a coach to
 * different places and say different sentences. */
export async function loadCoachProfile(
  coachId: string,
): Promise<{ profile: CoachProfile; exists: boolean } | { error: string }> {
  const { data, error } = await supabase
    .from("coach_profiles")
    .select("display_name,headline,bio,city,years_coaching,achievements,website,instagram,avatar_url,is_published")
    .eq("id", coachId)
    .maybeSingle();
  if (error) return { error: error.message };
  // ⚠️ NO ROW IS NOT AN ERROR — it is a coach who has not written their page yet, which is every
  // coach on their first visit. Returning an error here would greet them with a failure.
  if (data === null) return { profile: EMPTY_PROFILE, exists: false };
  return {
    exists: true,
    profile: {
      displayName: data.display_name ?? "",
      headline: data.headline ?? "",
      bio: data.bio ?? "",
      city: data.city ?? "",
      yearsCoaching: data.years_coaching ?? 0,
      achievements: (data.achievements as string[] | null) ?? [],
      website: data.website ?? "",
      instagram: data.instagram ?? "",
      avatarUrl: data.avatar_url,
      isPublished: data.is_published ?? false,
    },
  };
}

/* ⚠️ **SAVING IS NOT PUBLISHING**, and the phone's editor makes the same split: a draft may be
 * saved half-written, and `publishFault` is asked only when `publish` is true. Folding them would
 * mean a coach cannot save a page they are part-way through. */
export async function saveCoachProfile(
  coachId: string, p: CoachProfile, publish: boolean,
): Promise<string | null> {
  if (publish) {
    const fault = publishFault(p);
    if (fault !== null) return fault;
  }
  const { error } = await supabase.from("coach_profiles").upsert({
    id: coachId,
    display_name: p.displayName.trim(),
    headline: p.headline.trim(),
    bio: p.bio,
    city: p.city.trim(),
    years_coaching: p.yearsCoaching,
    achievements: keptAchievements(p.achievements),
    website: optional(p.website),
    instagram: optional(p.instagram),
    // Publishing is a one-way step here: unpublishing needs its own control and its own sentence,
    // and quietly clearing the flag on every save would retire a coach from the directory because
    // they fixed a typo.
    is_published: publish ? true : p.isPublished,
  });
  return error ? error.message : null;
}
